/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  ScrollView,
 View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Platform,
  Image,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import DateTimePicker from '@react-native-community/datetimepicker';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

// ==================== API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const mockProjects = [
  'Commercial Office Building - London',
  'Residential Complex - Manchester',
  'School Renovation - Birmingham',
  'Retail Fit-out - Leeds',
  'Healthcare Facility - Glasgow',
];

const mockRecipientTypes = ['Subcontractor', 'Supplier', 'Freelancer'];

const CreateInvoiceScreen = () => {
  const navigation = useNavigation();

  // State for company logo
  const [logoUrl, setLogoUrl] = useState(null);

  // Fetch company logo
  useEffect(() => {
    const loadCompanyLogo = async () => {
      try {
        const cachedProfile = await AsyncStorage.getItem('company_profile');
        if (cachedProfile) {
          const profile = JSON.parse(cachedProfile);
          setLogoUrl(profile.logo || null);
        }

        const companyId = await AsyncStorage.getItem('company_id');
        if (companyId) {
          const response = await api.get(API_ENDPOINTS.companyProfile, {
            params: { company_id: companyId },
          });
          if (response.data?.success && response.data.data && response.data.data.length > 0) {
            const latestProfile = response.data.data[0];
            setLogoUrl(latestProfile.logo || null);
            await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
          }
        }
      } catch (error) {
        console.warn('Error loading company logo:', error);
      }
    };
    loadCompanyLogo();
  }, []);

  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: '',
    recipientName: '',
    recipientType: mockRecipientTypes[0],
    projectName: mockProjects[0],
    issueDate: new Date(),
    dueDate: new Date(),
    amount: '',
    items: [],
    status: 'Draft',
    notes: '',
    attachments: [],
  });

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerField, setDatePickerField] = useState(null);

  const handleDateChange = (event, selectedDate) => {
    setShowDatePicker(Platform.OS === 'ios');
    if (selectedDate) {
      setInvoiceData({
        ...invoiceData,
        [datePickerField]: selectedDate,
      });
    }
  };

  const showDatepicker = field => {
    setShowDatePicker(true);
    setDatePickerField(field);
  };

  const handleAddItem = () => {
    setInvoiceData({
      ...invoiceData,
      items: [
        ...invoiceData.items,
        { description: '', quantity: '', unitPrice: '' },
      ],
    });
  };

  const handleRemoveItem = index => {
    const newItems = [...invoiceData.items];
    newItems.splice(index, 1);
    setInvoiceData({ ...invoiceData, items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...invoiceData.items];
    newItems[index][field] = value;
    setInvoiceData({ ...invoiceData, items: newItems });
  };

  const calculateTotalAmount = () => {
    return invoiceData.items
      .reduce((total, item) => {
        const quantity = parseFloat(item.quantity) || 0;
        const unitPrice = parseFloat(item.unitPrice) || 0;
        return total + quantity * unitPrice;
      }, 0)
      .toFixed(2);
  };

  const handleSubmit = () => {
    // Basic validation
    if (
      !invoiceData.invoiceNumber ||
      !invoiceData.recipientName ||
      !invoiceData.projectName ||
      invoiceData.items.length === 0
    ) {
      Alert.alert(
        'Error',
        'Please fill in all required fields and add at least one item.'
      );
      return;
    }

    // Here you would typically send the invoiceData to your backend or state management
    Alert.alert('Success', 'Invoice created successfully!');
    console.log('Invoice Data:', invoiceData);
    navigation.goBack();
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollViewContent}>
        {/* Header with dynamic logo */}
        <View style={styles.header}>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            style={styles.backButton}
          >
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>

          {logoUrl ? (
            <Image source={{ uri: (logoUrl && logoUrl.startsWith('http')) ? logoUrl : undefined }} style={styles.headerLogo} />
          ) : (
            <Ionicons name="document-text-outline" size={30} color="#fff" />
          )}

          <Text style={styles.title}>Create New Invoice</Text>

          <View style={{ width: 30 }} />
        </View>

        <View style={styles.formSection}>
          <Text style={styles.sectionTitle}>Invoice Details</Text>
          <TextInput
            style={styles.input}
            placeholder="Invoice Number"
            value={invoiceData.invoiceNumber}
            onChangeText={text =>
              setInvoiceData({ ...invoiceData, invoiceNumber: text })
            }
          />
          <TextInput
            style={styles.input}
            placeholder="Recipient Name"
            value={invoiceData.recipientName}
            onChangeText={text =>
              setInvoiceData({ ...invoiceData, recipientName: text })
            }
          />
          <View style={styles.pickerContainer}>
            <Picker
              selectedValue={invoiceData.recipientType}
              style={styles.picker}
              onValueChange={itemValue =>
                setInvoiceData({ ...invoiceData, recipientType: itemValue })
              }
            >
              {mockRecipientTypes.map((type, index) => (
                <Picker.Item key={index} label={type} value={type} />
              ))}
            </Picker>
          </View>
          <View style={styles.pickerContainer}>
            <Picker
              selectedValue={invoiceData.projectName}
              style={styles.picker}
              onValueChange={itemValue =>
                setInvoiceData({ ...invoiceData, projectName: itemValue })
              }
            >
              {mockProjects.map((project, index) => (
                <Picker.Item key={index} label={project} value={project} />
              ))}
            </Picker>
          </View>
        </View>

        <View style={styles.formSection}>
          <Text style={styles.sectionTitle}>Dates</Text>
          <TouchableOpacity
            onPress={() => showDatepicker('issueDate')}
            style={styles.dateInput}
          >
            <Text>
              Issue Date: {invoiceData.issueDate.toLocaleDateString()}
            </Text>
            <Ionicons name="calendar-outline" size={20} color="#666" />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => showDatepicker('dueDate')}
            style={styles.dateInput}
          >
            <Text>Due Date: {invoiceData.dueDate.toLocaleDateString()}</Text>
            <Ionicons name="calendar-outline" size={20} color="#666" />
          </TouchableOpacity>
          {showDatePicker && (
            <DateTimePicker
              testID="dateTimePicker"
              value={
                datePickerField === 'issueDate'
                  ? invoiceData.issueDate
                  : invoiceData.dueDate
              }
              mode="date"
              display="default"
              onChange={handleDateChange}
            />
          )}
        </View>

        <View style={styles.formSection}>
          <Text style={styles.sectionTitle}>Invoice Items</Text>
          {invoiceData.items.map((item, index) => (
            <View key={index} style={styles.itemRow}>
              <TextInput
                style={[styles.itemInput, { flex: 3 }]}
                placeholder="Description"
                value={item.description}
                onChangeText={text =>
                  handleItemChange(index, 'description', text)
                }
              />
              <TextInput
                style={[styles.itemInput, { flex: 1 }]}
                placeholder="Qty"
                keyboardType="numeric"
                value={item.quantity}
                onChangeText={text => handleItemChange(index, 'quantity', text)}
              />
              <TextInput
                style={[styles.itemInput, { flex: 2 }]}
                placeholder="Unit Price"
                keyboardType="numeric"
                value={item.unitPrice}
                onChangeText={text =>
                  handleItemChange(index, 'unitPrice', text)
                }
              />
              <TouchableOpacity
                onPress={() => handleRemoveItem(index)}
                style={styles.removeItemButton}
              >
                <Ionicons name="remove-circle-outline" size={24} color="red" />
              </TouchableOpacity>
            </View>
          ))}
          <TouchableOpacity
            onPress={handleAddItem}
            style={styles.addItemButton}
          >
            <Ionicons name="add-circle-outline" size={24} color="#0e4e96" />
            <Text style={styles.addItemButtonText}>Add Item</Text>
          </TouchableOpacity>
          <Text style={styles.totalAmount}>
            Total Amount: £{calculateTotalAmount()}
          </Text>
        </View>

        <View style={styles.formSection}>
          <Text style={styles.sectionTitle}>Additional Details</Text>
          <TextInput
            style={[styles.input, styles.textArea]}
            placeholder="Notes"
            multiline
            numberOfLines={4}
            value={invoiceData.notes}
            onChangeText={text =>
              setInvoiceData({ ...invoiceData, notes: text })
            }
          />
          {/* Attachment functionality can be added here */}
          <Text style={styles.attachmentText}>
            Attachments: {invoiceData.attachments.length} files
          </Text>
          <TouchableOpacity style={styles.attachButton}>
            <Ionicons name="attach-outline" size={20} color="#0e4e96" />
            <Text style={styles.attachButtonText}>Add Attachment</Text>
          </TouchableOpacity>
        </View>

        <TouchableOpacity onPress={handleSubmit} style={styles.submitButton}>
          <Text style={styles.submitButtonText}>Create Invoice</Text>
        </TouchableOpacity>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#4A90E2',
  },
  scrollViewContent: {
    padding: 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  backButton: {
    marginRight: 10,
  },
  headerLogo: {
    width: 30,
    height: 30,
    borderRadius: 15,
    resizeMode: 'cover',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#fff',
    flex: 1,
    textAlign: 'center',
  },
  formSection: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 15,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#0e4e96',
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 10,
    marginBottom: 10,
    fontSize: 16,
  },
  dateInput: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 10,
    marginBottom: 10,
    backgroundColor: '#fff',
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    marginBottom: 10,
    overflow: 'hidden',
  },
  picker: {
    height: 50,
    width: '100%',
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  itemInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 8,
    marginRight: 5,
    fontSize: 14,
  },
  removeItemButton: {
    marginLeft: 5,
  },
  addItemButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e0f7fa',
    padding: 10,
    borderRadius: 8,
    marginTop: 10,
  },
  addItemButtonText: {
    color: '#0e4e96',
    marginLeft: 5,
    fontWeight: 'bold',
  },
  totalAmount: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'right',
    marginTop: 15,
    color: '#333',
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  attachButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e0f7fa',
    padding: 10,
    borderRadius: 8,
    marginTop: 10,
  },
  attachButtonText: {
    color: '#0e4e96',
    marginLeft: 5,
    fontWeight: 'bold',
  },
  attachmentText: {
    fontSize: 16,
    color: '#666',
    marginBottom: 10,
  },
  submitButton: {
    backgroundColor: '#fdb913',
    padding: 15,
    borderRadius: 10,
    alignItems: 'center',
    marginTop: 20,
  },
  submitButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
  },
});

export default CreateInvoiceScreen;